import { useRef, useState, type ChangeEvent } from 'react'
import { authApi, describeError } from '@/lib/auth'
import { useAuthStore } from '@/store/auth'
import { useAppStore } from '@/store/app'
import { avatarUrl, fitAvatar, fitBanner, userBannerUrl } from '@/lib/avatar'
import { useProfileBanner } from '@/lib/profileBanner'
import { cx } from '@/lib/cx'
import { Avatar } from '../Avatar'
import { PlusMark } from '../PlusMark'
import { DisplayNameModal } from './AccountSection'
import layer from './SettingsLayer.module.css'
import styles from './ProfileSection.module.css'

/** Предел «о себе» — столько же принимает сервер (MaxBio). */
const MAX_BIO = 190

/**
 * Настройки → «Профиль»: то, что видят другие. Слева редактор, справа живая карточка —
 * сразу видно, как профиль выглядит в чужом клике по нику.
 */
export function ProfileSection() {
  const user = useAuthStore((s) => s.user)
  const setUser = useAuthStore((s) => s.setUser)
  const avatarRef = useRef<HTMLInputElement | null>(null)
  const bannerRef = useRef<HTMLInputElement | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [dialog, setDialog] = useState(false)
  const [bio, setBio] = useState(user?.bio ?? '')
  const [bioSaved, setBioSaved] = useState(false)
  const closeSettings = useAppStore((s) => s.closeSettings)
  const setView = useAppStore((s) => s.setView)
  const showContent = useAppStore((s) => s.showContent)
  const avatar = user ? avatarUrl(user.id, user.avatar) : undefined
  const bannerSrc = user ? userBannerUrl(user.id, user.banner) : undefined
  const bannerStyle = useProfileBanner(bannerSrc, avatar)
  if (!user) return null
  const name = user.global_name ?? user.username

  const save = async (patch: Parameters<typeof authApi.updateProfile>[0]) => {
    setBusy(true)
    setError(null)
    try {
      setUser(await authApi.updateProfile(patch))
      return true
    } catch (err) {
      setError(describeError(err).message)
      return false
    } finally {
      setBusy(false)
    }
  }
  const pickFile = async (e: ChangeEvent<HTMLInputElement>, kind: 'avatar' | 'banner') => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    try {
      const data = kind === 'avatar' ? await fitAvatar(file) : await fitBanner(file)
      await save(kind === 'avatar' ? { avatar: data } : { banner: data })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось прочитать картинку')
    }
  }
  const saveBio = async () => {
    if (await save({ bio })) {
      setBioSaved(true)
      setTimeout(() => setBioSaved(false), 2000)
    }
  }
  const bioLeft = MAX_BIO - [...bio].length

  return (
    <section>
      <h1 className={layer.sectionTitle}>Профиль</h1>
      <p className={layer.text}>Так вас видят другие: имя, аватар, баннер и строчка о себе.</p>

      <div className={styles.split}>
        <div className={styles.editor}>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="profile-name">Отображаемое имя</label>
            <div className={styles.inline}>
              <span className={styles.value} id="profile-name">{user.global_name ?? `— не задано, показываем @${user.username} —`}</span>
              <button type="button" className={cx(layer.button, layer.buttonSecondary)} onClick={() => setDialog(true)}>Изменить</button>
            </div>
          </div>

          <div className={styles.field}>
            <span className={styles.label}>Аватар</span>
            <div className={styles.inline}>
              <button type="button" className={layer.button} disabled={busy} onClick={() => avatarRef.current?.click()}>
                {user.avatar ? 'Заменить' : 'Загрузить'}
              </button>
              {user.avatar ? (
                <button type="button" className={cx(layer.button, layer.buttonSecondary)} disabled={busy} onClick={() => void save({ avatar: null })}>Удалить</button>
              ) : null}
              <span className={styles.hint}>PNG, JPEG, GIF или WebP, до 1 МБ</span>
            </div>
            <input ref={avatarRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp" hidden onChange={(e) => void pickFile(e, 'avatar')} />
          </div>

          <div className={styles.field}>
            <span className={styles.label}>Баннер</span>
            <div className={styles.inline}>
              <button type="button" className={layer.button} disabled={busy} onClick={() => bannerRef.current?.click()}>
                {user.banner ? 'Заменить' : 'Загрузить'}
              </button>
              {user.banner ? (
                <button type="button" className={cx(layer.button, layer.buttonSecondary)} disabled={busy} onClick={() => void save({ banner: null })}>Удалить</button>
              ) : null}
              <span className={styles.hint}>Широкая картинка, обрежем до 1200×400</span>
            </div>
            <input ref={bannerRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp" hidden onChange={(e) => void pickFile(e, 'banner')} />
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="profile-bio">О себе</label>
            <textarea
              id="profile-bio"
              className={styles.bio}
              value={bio}
              maxLength={MAX_BIO}
              rows={4}
              placeholder="Пара слов о себе — их видно в карточке профиля"
              onChange={(e) => setBio(e.target.value)}
            />
            <div className={styles.inline}>
              <button type="button" className={layer.button} disabled={busy || bio === (user.bio ?? '')} onClick={() => void saveBio()}>
                {bioSaved ? 'Сохранено' : 'Сохранить'}
              </button>
              <span className={cx(styles.hint, bioLeft < 0 && styles.hintDanger)}>осталось {bioLeft}</span>
            </div>
          </div>

          {/* магазин — на главной, рядом с «Друзьями»; отсюда туда одна кнопка */}
          <div className={styles.field}>
            <span className={styles.label}>Украшения</span>
            <div className={styles.inline}>
              <button
                type="button"
                className={cx(layer.button, layer.buttonSecondary)}
                onClick={() => {
                  closeSettings()
                  setView({ kind: 'home', page: 'shop' })
                  showContent() // на телефоне — сразу магазин, а не список бесед
                }}
              >
                Открыть магазин
              </button>
              <span className={styles.hint}>Рамка аватара, эффект профиля, табличка и стиль ника</span>
            </div>
          </div>

          {error ? <p className={styles.error} role="alert">{error}</p> : null}
        </div>

        <div className={styles.preview}>
          <span className={styles.previewLabel}>Предпросмотр</span>
          <div className={styles.card}>
            <div className={styles.banner} style={bannerStyle} />
            <div className={styles.cardHead}>
              <Avatar initial={name.slice(0, 1).toUpperCase()} color="#5865F2" size={80} className={styles.cardAvatar} src={avatar} decoration={user.avatar_decoration_data} />
            </div>
            <div className={styles.cardBody}>
              <div className={styles.displayName}>
                {name}
                {user.premium_type > 0 ? <PlusMark size={16} className={styles.plus} /> : null}
              </div>
              <div className={styles.username}>@{user.username}</div>
              {bio.trim() ? <p className={styles.cardBio}>{bio}</p> : null}
              <div className={styles.since}>В Кличате с {new Date(user.created_at).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
            </div>
          </div>
        </div>
      </div>

      {dialog ? <DisplayNameModal current={user.global_name ?? ''} onClose={() => setDialog(false)} /> : null}
    </section>
  )
}
